import { createSWRJobManager, runSWRDirectJob as runDirectJob, isSWRAbortError } from './swrJobManager';
import { swrActivityMonitor, describeSWRActivity } from './swrActivityMonitor';

const jobs = createSWRJobManager({
  createWorker: () => new Worker(new URL('../workers/swrSimulationWorker.js', import.meta.url)),
  maxConcurrent: 2,
  onActivity: event => {
    if (event.status === 'running') swrActivityMonitor.start(event.activityId, event.active);
    if (event.status === 'queued') swrActivityMonitor.update(event.activityId, { status: 'queued' });
    if (event.status === 'complete') {
      if (event.completeExternally) swrActivityMonitor.update(event.activityId, { workerActive: false, status: 'finalizing', phase: 'Salvataggio finale dei file' });
      else swrActivityMonitor.finish(event.activityId, 'complete');
    }
    if (['error', 'cancelled'].includes(event.status)) swrActivityMonitor.update(event.activityId, { workerActive: false });
  },
});

const directActivities = new WeakMap();
const observeResult = async (promise, id, external) => {
  try {
    const result = await promise;
    if (external) swrActivityMonitor.update(id, { workerActive: false, status: 'finalizing', phase: 'Salvataggio finale dei file' });
    else swrActivityMonitor.finish(id, 'complete');
    return result;
  } catch (error) {
    if (!external) swrActivityMonitor.finish(id, isSWRAbortError(error) ? 'cancelled' : 'error', error.message);
    throw error;
  }
};
const runWorkerJob = (type, params, onProgress = () => {}, extra = {}, execution = {}) => {
  const id = execution.activityId || swrActivityMonitor.create(describeSWRActivity(type, params, extra.options),
    { context: execution.activityContext || swrActivityMonitor.newRun() });
  if (execution.activityId) swrActivityMonitor.update(id, describeSWRActivity(type, params, extra.options));
  const report = progress => { swrActivityMonitor.progress(id, progress); onProgress?.(progress); };
  if (typeof Worker === 'undefined') { directActivities.set(execution, { id, report }); return null; }
  return observeResult(jobs.run(type, params, report, extra, { ...execution, activityId: id }), id, execution.completeExternally);
};
const runSWRDirectJob = (run, onProgress, execution = {}) => {
  const info = directActivities.get(execution); directActivities.delete(execution);
  if (!info) return runDirectJob(run, onProgress, execution);
  swrActivityMonitor.start(info.id);
  return observeResult(runDirectJob(run, info.report, execution), info.id, execution.completeExternally);
};

export const generateSWRExportParts = async (params, options = {}, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('generate-swr-parts', params, onProgress, { options }, execution);
  if (workerPromise) return workerPromise;
  return runSWRDirectJob(async report => {
    const { streamSWRExportDataset } = await import('../utils/swrStreamingExport');
    return streamSWRExportDataset(params, { ...options, signal: execution.signal, onPart: execution.onPart,
      onManifest: execution.onManifest, onProgress: report });
  }, onProgress, execution);
};

export const generateSWRExportBundle = async (params, options = {}, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('generate-swr-bundle', params, onProgress, { options }, execution);
  if (workerPromise) return workerPromise;
  return runSWRDirectJob(async report => {
    const { streamSWRExportBundle } = await import('../utils/swrExportBundle');
    return streamSWRExportBundle(params, { ...options, signal: execution.signal, onPart: execution.onPart,
      onManifest: execution.onManifest, onBundleManifest: execution.onBundleManifest,
      onDatasetComplete: execution.onDatasetComplete, onProgress: report });
  }, onProgress, execution);
};

export const runSafeWithdrawalSimulation = async (params, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('run-swr-simulation', params, onProgress, {}, execution);
  if (workerPromise) return workerPromise;

  return runSWRDirectJob(async report => {
    const { runSafeWithdrawalSimulation: runSafeWithdrawalSimulationDirect } = await import('./simulation/swrEngine');
    return runSafeWithdrawalSimulationDirect(
      {
        ...params,
        includeRawSimulationData: false,
        enableSafeWithdrawalAnalysis: false,
      },
      report
    );
  }, onProgress, execution);
};

export const runSWRSafeWithdrawalAnalysis = async (params, options = {}, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('run-swr-safe-withdrawal', params, onProgress, { options }, execution);
  if (workerPromise) return workerPromise;

  return runSWRDirectJob(async report => {
    const { computeSWRSafeWithdrawalAnalysis } = await import('./simulation/swrEngine');
    return computeSWRSafeWithdrawalAnalysis(params, options, report);
  }, onProgress, execution);
};

export const runSWRStressTests = async (params, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('run-swr-stress-tests', params, onProgress, {}, execution);
  if (workerPromise) return workerPromise;

  return runSWRDirectJob(async report => {
    const { runSWRStressTests: runSWRStressTestsDirect } = await import('./simulation/swrEngine');
    return runSWRStressTestsDirect(params, report);
  }, onProgress, execution);
};

export const generateSWRSimulationsCSV = async (params, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('generate-swr-csv', params, onProgress, {}, execution);
  if (workerPromise) return workerPromise;

  return runSWRDirectJob(async report => {
    const { runSafeWithdrawalSimulation: runSafeWithdrawalSimulationDirect } = await import('./simulation/swrEngine');
    const { buildSWRSimulationsCSV } = await import('../utils/swrCsvBuilder');
    const getProgressPercent = (progress) => (
      typeof progress === 'object' ? progress?.percent || 0 : progress || 0
    );
    const result = await runSafeWithdrawalSimulationDirect(
      {
        ...params,
        includeRawSimulationData: true,
        enableSafeWithdrawalAnalysis: false,
      },
      (progress) => report({
        percent: Math.round(getProgressPercent(progress) * 0.75),
        phase: 'Rigenerazione simulazioni per CSV',
      })
    );
    return buildSWRSimulationsCSV(
      result.rawSimulationData,
      {
        ...params,
        engineModelVersion: result.engineModelVersion,
        provenance: result.provenance,
        returnParameterUncertainty: result.returnParameterUncertainty,
        drawdownStats: result.drawdownStats,
        macroMode: result.macroMode,
        macroSeed: result.macroSeed,
        macroAssumptions: result.macroAssumptions,
        macroSummary: result.macroSummary,
            historicalPortfolioSummary: result.historicalPortfolio,
      },
      (progress) => report({
        percent: 75 + Math.round(progress * 0.25),
        phase: 'Preparazione CSV completo',
      })
    );
  }, onProgress, execution);
};

export const generateSWRStressTestCSVs = async (params, onProgress = () => {}, execution = {}) => {
  const workerPromise = runWorkerJob('generate-swr-stress-csvs', params, onProgress, {}, execution);
  if (workerPromise) return workerPromise;

  return runSWRDirectJob(async report => {
    const {
      buildSWRStressBaselineParams,
      buildSWRStressTestParams,
      getSWRStressTestDefinitions,
      runSafeWithdrawalSimulation: runSafeWithdrawalSimulationDirect,
    } = await import('./simulation/swrEngine');
    const { buildSWRSimulationsCSV } = await import('../utils/swrCsvBuilder');
    const definitions = getSWRStressTestDefinitions(params);
    const files = [];
    const totalJobs = definitions.length + 1;
    const getProgressPercent = (progress) => (
      typeof progress === 'object' ? progress?.percent || 0 : progress || 0
    );
    const slugify = (value) => String(value || 'stress-test')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'stress-test';

    const weight = 100 / totalJobs;
    const baselineParams = {
      ...buildSWRStressBaselineParams(params, { includeRawSimulationData: true }),
      isStressBaseline: true,
    };
    const baselineResult = await runSafeWithdrawalSimulationDirect(
      baselineParams,
      (progress) => report({
        percent: Math.round(getProgressPercent(progress) * 0.70 * (weight / 100)),
        phase: 'Rigenerazione baseline stress 3000',
      })
    );
    const baselineCsv = await buildSWRSimulationsCSV(
      baselineResult.rawSimulationData,
      {
        ...baselineParams,
        engineModelVersion: baselineResult.engineModelVersion,
        provenance: baselineResult.provenance,
        returnParameterUncertainty: baselineResult.returnParameterUncertainty,
        drawdownStats: baselineResult.drawdownStats,
        macroMode: baselineResult.macroMode,
        macroSeed: baselineResult.macroSeed,
        macroAssumptions: baselineResult.macroAssumptions,
        macroSummary: baselineResult.macroSummary,
            historicalPortfolioSummary: baselineResult.historicalPortfolio,
      },
      (progress) => report({
        percent: Math.round(weight * 0.70 + progress * 0.30 * (weight / 100)),
        phase: 'Preparazione CSV baseline stress 3000',
      })
    );
    files.push({
      name: '00_baseline_stress_3000.csv',
      sheetName: 'Baseline stress 3000',
      content: baselineCsv,
    });

    for (let index = 0; index < definitions.length; index++) {
      const definition = definitions[index];
      const basePercent = (index + 1) * weight;
      const stressParams = buildSWRStressTestParams(params, definition, { includeRawSimulationData: true });
      const result = await runSafeWithdrawalSimulationDirect(
        stressParams,
        (progress) => report({
          percent: Math.round(basePercent + getProgressPercent(progress) * 0.70 * (weight / 100)),
          phase: `Rigenerazione ${definition.name}`,
        })
      );
      const csv = await buildSWRSimulationsCSV(
        result.rawSimulationData,
        {
          ...stressParams,
          engineModelVersion: result.engineModelVersion,
          provenance: result.provenance,
          returnParameterUncertainty: result.returnParameterUncertainty,
          drawdownStats: result.drawdownStats,
          macroMode: result.macroMode,
          macroSeed: result.macroSeed,
          macroAssumptions: result.macroAssumptions,
          macroSummary: result.macroSummary,
            historicalPortfolioSummary: result.historicalPortfolio,
        },
        (progress) => report({
          percent: Math.round(basePercent + weight * 0.70 + progress * 0.30 * (weight / 100)),
          phase: `Preparazione CSV ${definition.name}`,
        })
      );
      files.push({
        name: `${String(index + 1).padStart(2, '0')}_${slugify(definition.name)}.csv`,
        sheetName: definition.name,
        content: csv,
      });
    }

    report({ percent: 100, phase: 'Stress test esportati' });
    return files;
  }, onProgress, execution);
};
